import { defineStore } from 'pinia';
import type {
    LocaleStoreState, LocaleStoreGetters, LocaleStoreActions,
} from '@/types/stores/LocaleStoreTypes';
import type { LanguageType } from '@/types/LanguageType';
import type { CurrencyType } from '@/types/CurrencyType';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type { CustomResponseType } from '@/types/CustomAxiosReponseType';

const CACHE_DURATION = 1000 * 60 * 60;

export const useLocaleStore = defineStore<'locale', LocaleStoreState, LocaleStoreGetters, LocaleStoreActions>(
    'locale',
    {
        state: (): LocaleStoreState => ({
            languages: [],
            currencies: [],
            isLoading: false,
            lastFetched: {
                languages: null,
                currencies: null,
            },
        }),
        getters: {
            getLanguageByCode:
                (state: LocaleStoreState) =>
                    (code: string): LanguageType | undefined =>
                        state.languages.find(lang => lang.code === code),
            getCurrencyByCode:
                (state: LocaleStoreState) =>
                    (code: string): CurrencyType | undefined =>
                        state.currencies.find(curr => curr.code === code),
            hasLanguages: (state: LocaleStoreState): boolean => state.languages.length > 0,
            hasCurrencies: (state: LocaleStoreState): boolean => state.currencies.length > 0,
        },
        actions: {
            shouldFetchData(force: boolean, dataLength: number, lastFetchTime: number | null): boolean {
                if (force || dataLength === 0 || !lastFetchTime) {
                    return true;
                }

                const now = Date.now();

                return now - lastFetchTime > CACHE_DURATION;
            },

            async fetchLanguages(force = false): Promise<void> {
                if (!this.shouldFetchData(force, this.languages.length, this.lastFetched.languages)) {
                    return;
                }

                this.isLoading = true;
                const now = Date.now();

                await handleRequest({
                    method: RequestMethodEnum.Get,
                    endpoint: '/locales',
                    onSuccess: (response: CustomResponseType<LanguageType[]>) => {
                        this.languages = response.data.data.sort((a, b) => a.name.localeCompare(b.name));
                        this.lastFetched.languages = now;
                    },
                });

                this.isLoading = false;
            },

            async fetchCurrencies(force = false): Promise<void> {
                if (!this.shouldFetchData(force, this.currencies.length, this.lastFetched.currencies)) {
                    return;
                }

                this.isLoading = true;
                const now = Date.now();

                await handleRequest({
                    method: RequestMethodEnum.Get,
                    endpoint: '/currencies',
                    onSuccess: (response: CustomResponseType<CurrencyType[]>) => {
                        this.currencies = response.data.data.sort((a, b) => a.code.localeCompare(b.code));
                        this.lastFetched.currencies = now;
                    },
                });

                this.isLoading = false;
            },

            async fetchAll(force = false): Promise<void> {
                await Promise.all([this.fetchLanguages(force), this.fetchCurrencies(force)]);
            },
        },
    },
);
